import {
  FormatAlignCenterIconData,
  FormatAlignLeftIconData,
  FormatAlignRightIconData,
} from "@meiden-connect/material-symbols";
import { Button, Icon } from "@meiden-connect/ui";
import type { ReactNode } from "react";
import type { TextAlign } from "@refinement-canvas/domain";
import { isValidHexColor, normalizeHexColor } from "../../../lib/hex-color";
import {
  TEXT_COLOR_PRESET_COLORS,
  TEXT_FILL_PRESET_COLORS,
  TEXT_FILL_TRANSPARENT,
} from "../../../lib/text-color";
import {
  ColorSwatchList,
  ColorValuePreview,
  SubPanel,
  TextAlignChooser,
  TextSizeChooser,
} from "./toolbar-controls";
import type { ToolbarParentItem } from "./toolbar-parent-child-panel";
import type { CustomColorPalettes } from "./use-custom-color-palettes";

// テキストの書式項目を、配置前パネル（tool-bar.tsx）と選択後パネル（selection-style-panel.tsx）が
// 共有するための正本。項目の順序・ラベル・プレビュー・子パネルの選択肢・太字ボタンをここへ集約し、
// 「操作場所ごとに項目集合や並び順が違う」状態が構造的に起きないようにする。
//
// 操作対象は共有しない。配置前パネルは「今後作るオブジェクトの既定値」だけを、選択後パネルは
// 「選択中オブジェクト」だけを変更する（非対称性は各パネル側のハンドラが担う）。

/**
 * テキストの親項目キー。太字は子パネルを開かない直接操作のため、この一覧には含めず
 * 親の末尾（directActions）へ置く。
 */
export type TextStyleItemKey = "text-color" | "text-fill" | "text-align" | "text-size";

/** 親トリガーの並び順（両パネル共通）。 */
export const TEXT_STYLE_ITEM_ORDER: readonly TextStyleItemKey[] = [
  "text-color",
  "text-fill",
  "text-align",
  "text-size",
];

/** 任意の項目キーがテキストの共有項目かを判定する（各パネルの広いキー union から絞り込む）。 */
export function isTextStyleItemKey(key: string): key is TextStyleItemKey {
  return (TEXT_STYLE_ITEM_ORDER as readonly string[]).includes(key);
}

/** 親トリガーと子パネルの見出しに使うラベル（両パネル共通）。 */
export const TEXT_STYLE_ITEM_LABELS: Readonly<Record<TextStyleItemKey, string>> = {
  "text-color": "文字の色",
  "text-fill": "背景の色",
  "text-align": "文字寄せ",
  "text-size": "文字サイズ",
};

/** 太字の直接操作ボタンの読み上げ名（両パネル共通）。 */
export const TEXT_STYLE_BOLD_LABEL = "太字";

/** 背景色の子パネルに出す「背景なし」の選択肢（両パネル共通）。 */
const TEXT_FILL_NONE_OPTION = {
  value: TEXT_FILL_TRANSPARENT,
  label: "背景なし",
} as const;

/** テキストの現在値。複数選択で値が割れている項目は null（混在）で渡す。 */
export interface TextStyleValues {
  readonly color: string | null;
  readonly fill: string | null;
  readonly textAlign: TextAlign | null;
  readonly fontSize: number | null;
}

/** 子パネルで値を選んだときの反映先。配置前は既定値、選択後は選択中オブジェクトへ向ける。 */
export interface TextStyleHandlers {
  readonly onChooseColor: (color: string) => void;
  readonly onChooseFill: (fill: string) => void;
  readonly onChooseAlign: (align: TextAlign) => void;
  readonly onChooseFontSize: (px: number) => void;
}

/** 子パネルの描画に必要な入力一式。 */
export interface TextStyleChildInput extends TextStyleValues, TextStyleHandlers {
  /** 子パネルの data-testid の接頭辞（`placement-style` / `selection-style`）。 */
  readonly testIdPrefix: string;
  /** 色見本一覧の読み上げ名の接頭辞（例: `テキストの` / `選択中のテキストの`）。 */
  readonly ariaLabelPrefix: string;
  /** カスタム色の正本。文字色は枠線色パレット、背景色は背景色パレットを両パネルで共有する。 */
  readonly palettes: CustomColorPalettes;
}

/** 文字寄せの現在値プレビュー。混在時はダッシュを出す。 */
export function textStyleAlignPreview(value: TextAlign | null): ReactNode {
  if (value === null) return "—";
  return (
    <Icon
      d={
        value === "left"
          ? FormatAlignLeftIconData
          : value === "center"
            ? FormatAlignCenterIconData
            : FormatAlignRightIconData
      }
    />
  );
}

/**
 * 文字サイズの現在値プレビュー。最大 3 桁（4〜128）と混在時のダッシュを、
 * 正方形のトリガー内へ改行させずに収めるため数値専用の表示へ包む。
 */
export function textStyleFontSizePreview(value: number | null): ReactNode {
  return <span className="canvas-toolbar-parent-number">{value ?? "—"}</span>;
}

/** 太字の直接操作ボタン。両パネルで同じ寸法・variant・ARIA を使う。 */
export function TextStyleBoldButton({
  pressed,
  testId,
  shortcut,
  onToggle,
}: {
  /** null は複数選択で太字が割れている状態。 */
  readonly pressed: boolean | null;
  readonly testId: string;
  /** 選択後など、この直接操作と同じ処理を行うショートカットの表示名。 */
  readonly shortcut?: string | undefined;
  readonly onToggle: () => void;
}): JSX.Element {
  return (
    <Button
      type="button"
      // 表示文言どおりの処理をクリック直後に実行するため action を使い、
      // 有効・混在は選択中のアイコンボタンと同じ emphasis で強調する。
      variant={pressed === true || pressed === null ? "action emphasis" : "action"}
      className="canvas-icon-button canvas-toolbar-parent-trigger canvas-toolbar-square-button"
      aria-label={TEXT_STYLE_BOLD_LABEL}
      title={shortcut ? `${TEXT_STYLE_BOLD_LABEL} (${shortcut})` : TEXT_STYLE_BOLD_LABEL}
      aria-pressed={pressed === null ? "mixed" : pressed}
      data-testid={testId}
      onClick={onToggle}
    >
      <strong aria-hidden="true">B</strong>
    </Button>
  );
}

/** 共有の順序・ラベル・プレビューから、テキストの親トリガー一覧を組み立てる。 */
export function textStyleParentItems(
  values: TextStyleValues
): readonly ToolbarParentItem<TextStyleItemKey>[] {
  return TEXT_STYLE_ITEM_ORDER.map((key) => ({
    key,
    label: TEXT_STYLE_ITEM_LABELS[key],
    mixed: textStyleItemValue(key, values) === null,
    preview: textStyleItemPreview(key, values),
  }));
}

/** 項目キーに対応する現在値。混在（null）判定を 1 か所へ集約する。 */
function textStyleItemValue(key: TextStyleItemKey, values: TextStyleValues): string | number | null {
  switch (key) {
    case "text-color":
      return values.color;
    case "text-fill":
      return values.fill;
    case "text-align":
      return values.textAlign;
    case "text-size":
      return values.fontSize;
  }
}

function textStyleItemPreview(key: TextStyleItemKey, values: TextStyleValues): ReactNode {
  switch (key) {
    case "text-color":
      return <ColorValuePreview value={values.color} underline />;
    case "text-fill":
      return <ColorValuePreview value={values.fill} />;
    case "text-align":
      return textStyleAlignPreview(values.textAlign);
    case "text-size":
      return textStyleFontSizePreview(values.fontSize);
  }
}

/**
 * 「＋」のカラーピッカーで確定した任意色を、パレットへ 1 件追加してからそのまま選ぶ。
 * 無効な値は既存の hex 判定で弾き、追加も選択も行わない。
 */
function addThenChoose(
  value: string,
  addToPalette: (color: string) => void,
  choose: (color: string) => void
): void {
  if (!isValidHexColor(value)) return;
  const normalized = normalizeHexColor(value);
  addToPalette(normalized);
  choose(normalized);
}

/** テキストの子パネルを 1 枚描画する。プリセット・カスタム色・選択肢は両パネルで同一になる。 */
export function renderTextStyleChild(
  key: TextStyleItemKey,
  input: TextStyleChildInput
): JSX.Element {
  if (key === "text-align") {
    const label = TEXT_STYLE_ITEM_LABELS[key];
    return (
      <SubPanel label={label} testId={`${input.testIdPrefix}-${key}`}>
        <TextAlignChooser value={input.textAlign} onChange={input.onChooseAlign} />
      </SubPanel>
    );
  }
  return renderTextStyleValueChild(key, input);
}

/**
 * 文字寄せを除く 3 項目（文字の色・背景の色・文字サイズ）の子パネル。
 * 全文用パネルと範囲専用パネルが同じ選択肢・同じ見出しを共有するための正本。
 */
function renderTextStyleValueChild(
  key: Exclude<TextStyleItemKey, "text-align">,
  input: TextRangeStyleChildInput
): JSX.Element {
  const label = TEXT_STYLE_ITEM_LABELS[key];
  const testId = `${input.testIdPrefix}-${key}`;
  const allowCustomColorEditing = input.allowCustomColorEditing ?? true;
  switch (key) {
    case "text-color":
      return (
        <SubPanel label={label} testId={testId}>
          <ColorSwatchList
            ariaLabel={`${input.ariaLabelPrefix}${label}`}
            presetColors={TEXT_COLOR_PRESET_COLORS}
            customColors={input.palettes.shapeStroke.colors}
            value={input.color}
            onChoose={input.onChooseColor}
            onAddColor={(value) =>
              addThenChoose(value, input.palettes.shapeStroke.add, input.onChooseColor)
            }
            onUpdateCustomColor={input.palettes.shapeStroke.update}
            onDeleteCustomColor={input.palettes.shapeStroke.remove}
            allowCustomColorEditing={allowCustomColorEditing}
          />
        </SubPanel>
      );
    case "text-fill":
      return (
        <SubPanel label={label} testId={testId}>
          <ColorSwatchList
            ariaLabel={`${input.ariaLabelPrefix}${label}`}
            presetColors={TEXT_FILL_PRESET_COLORS}
            customColors={input.palettes.shape.colors}
            value={input.fill}
            onChoose={input.onChooseFill}
            onAddColor={(value) =>
              addThenChoose(value, input.palettes.shape.add, input.onChooseFill)
            }
            onUpdateCustomColor={input.palettes.shape.update}
            onDeleteCustomColor={input.palettes.shape.remove}
            noneOption={TEXT_FILL_NONE_OPTION}
            allowCustomColorEditing={allowCustomColorEditing}
          />
        </SubPanel>
      );
    case "text-size":
      return (
        <SubPanel label={label} testId={testId}>
          <TextSizeChooser value={input.fontSize} onChange={input.onChooseFontSize} />
        </SubPanel>
      );
  }
}

//------------------------------------------------------------------------------
// テキスト範囲専用パネル（文字寄せを除く 3 項目 + 直接操作の太字）
//------------------------------------------------------------------------------

/** 範囲専用パネルの親項目キー。文字寄せは行・図形全体の概念のため含めない。 */
export type TextRangeStyleItemKey = Exclude<TextStyleItemKey, "text-align">;

/** 範囲専用パネルの親トリガーの並び順（全文用パネルから文字寄せだけを除いたもの）。 */
export const TEXT_RANGE_STYLE_ITEM_ORDER: readonly TextRangeStyleItemKey[] =
  TEXT_STYLE_ITEM_ORDER.filter(
    (key): key is TextRangeStyleItemKey => key !== "text-align"
  );

/** 範囲専用パネルの現在値。混在している項目は null で渡す。 */
export type TextRangeStyleValues = Omit<TextStyleValues, "textAlign">;

/** 範囲専用パネルの子パネル入力（文字寄せの値・ハンドラを持たない）。 */
export interface TextRangeStyleChildInput
  extends TextRangeStyleValues,
    Omit<TextStyleHandlers, "onChooseAlign"> {
  readonly testIdPrefix: string;
  readonly ariaLabelPrefix: string;
  readonly palettes: CustomColorPalettes;
  /** 任意色の追加・変更・削除 UI を出すか（範囲専用パネルでは false）。 */
  readonly allowCustomColorEditing?: boolean | undefined;
}

/** 共有の順序・ラベル・プレビューから、範囲専用パネルの親トリガー一覧を組み立てる。 */
export function textRangeStyleParentItems(
  values: TextRangeStyleValues
): readonly ToolbarParentItem<TextRangeStyleItemKey>[] {
  return TEXT_RANGE_STYLE_ITEM_ORDER.map((key) => ({
    key,
    label: TEXT_STYLE_ITEM_LABELS[key],
    mixed: textStyleItemValue(key, { ...values, textAlign: null }) === null,
    preview: textStyleItemPreview(key, { ...values, textAlign: null }),
  }));
}

/** 範囲専用パネルの子パネルを 1 枚描画する（全文用パネルと同じ選択肢・見出しを共有する）。 */
export function renderTextRangeStyleChild(
  key: TextRangeStyleItemKey,
  input: TextRangeStyleChildInput
): JSX.Element {
  return renderTextStyleValueChild(key, input);
}

/** 任意の項目キーが範囲専用パネルの項目かを判定する。 */
export function isTextRangeStyleItemKey(key: string): key is TextRangeStyleItemKey {
  return (TEXT_RANGE_STYLE_ITEM_ORDER as readonly string[]).includes(key);
}
